import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Shield, 
  Users, 
  Search,
  Eye,
  Edit,
  LogOut,
  LayoutDashboard,
  ShoppingBag,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Ban,
  ClipboardCheck,
  Trash2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [subscription, setSubscription] = useState("all");
  const [accountType, setAccountType] = useState("all");
  const [skip, setSkip] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ subscription_plan: "" });
  const [saving, setSaving] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
      if (search) params.append("search", search);
      if (subscription !== "all") params.append("subscription", subscription);
      if (accountType !== "all") params.append("account_type", accountType);
      
      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  }, [skip, search, subscription, accountType, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchUsers();
  }, [fetchUsers, navigate]);

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
      });
      setSelectedUser(response.data);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const handleEditUser = (user) => {
    setEditUser(user);
    setEditForm({ subscription_plan: user.subscription_plan });
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${editUser.user_id}`, editForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
      });
      toast.success("User updated successfully");
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleAccountAction = async (userId, action) => {
    const token = localStorage.getItem("adminToken");
    try {
      await axios.put(`${API}/admin/users/${userId}`, { account_status: action }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Account ${action === "suspended" ? "suspended" : action === "under_review" ? "set to under review" : "activated"}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update account status");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteUser.user_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
      });
      toast.success(`User ${deleteUser.email} deleted`);
      setDeleteUser(null);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    navigate("/admin/login");
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case "enterprise": return "bg-violet-500/10 text-violet-400 border-violet-500/30";
      case "professional": return "bg-primary/10 text-primary border-primary/30";
      default: return "bg-slate-700/50 text-slate-400 border-slate-600";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "suspended": return "bg-red-500/10 text-red-400 border-red-500/30";
      case "under_review": return "bg-amber-500/10 text-amber-400 border-amber-500/30";
      default: return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Admin Navbar */}
      <nav className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-violet-400" />
              </div>
              <span className="text-lg font-bold text-white">Admin Portal</span>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to="/admin/dashboard">
            <Button variant="outline" className="btn-secondary">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
          <Link to="/admin/users">
            <Button className="bg-violet-600 hover:bg-violet-700 text-white">
              <Users className="w-4 h-4 mr-2" />
              Users
            </Button>
          </Link>
          <Link to="/admin/requests">
            <Button variant="outline" className="btn-secondary">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Service Requests
            </Button>
          </Link>
          <Link to="/admin/transactions">
            <Button variant="outline" className="btn-secondary">
              <CreditCard className="w-4 h-4 mr-2" />
              Transactions
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card className="card-dashboard mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
                  className="pl-10 input-default"
                  data-testid="search-users-input"
                />
              </div>
              <Select value={subscription} onValueChange={(v) => { setSubscription(v); setSkip(0); }}>
                <SelectTrigger className="w-full sm:w-44 input-default">
                  <SelectValue placeholder="Filter by plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
              <Select value={accountType} onValueChange={(v) => { setAccountType(v); setSkip(0); }}>
                <SelectTrigger className="w-full sm:w-44 input-default" data-testid="filter-account-type-select">
                  <SelectValue placeholder="Account Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                  <SelectItem value="estate">Estate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-400" />
                Users ({total})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No users found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">User</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Account Type</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Subscription</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Joined</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.user_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-4 px-4">
                            <div>
                              <p className="text-white font-medium">{user.first_name} {user.last_name}</p>
                              <p className="text-slate-400 text-sm">{user.email}</p>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-slate-300 capitalize">{user.account_type}</span>
                          </td>
                          <td className="py-4 px-4">
                            <Badge className={`capitalize ${getPlanBadge(user.subscription_plan)}`}>
                              {user.subscription_plan}
                            </Badge>
                          </td>
                          <td className="py-4 px-4">
                            <Badge className={`capitalize ${getStatusBadge(user.account_status)}`}>
                              {(user.account_status || "active").replace("_", " ")}
                            </Badge>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-slate-400 text-sm">
                              {new Date(user.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => fetchUserDetails(user.user_id)}
                                className="text-slate-400 hover:text-white"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditUser(user)}
                                className="text-slate-400 hover:text-white"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {user.account_status !== "suspended" ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAccountAction(user.user_id, "suspended")}
                                  className="text-amber-400 hover:text-amber-300"
                                  title="Suspend"
                                  data-testid={`suspend-user-${user.user_id}`}
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAccountAction(user.user_id, "active")}
                                  className="text-emerald-400 hover:text-emerald-300"
                                  title="Activate"
                                  data-testid={`activate-user-${user.user_id}`}
                                >
                                  <ClipboardCheck className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteUser(user)}
                                className="text-red-400 hover:text-red-300"
                                title="Delete"
                                data-testid={`delete-user-${user.user_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
                  <p className="text-slate-400 text-sm">
                    Showing {skip + 1} - {Math.min(skip + limit, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSkip(Math.max(0, skip - limit))}
                      disabled={skip === 0}
                      className="btn-secondary"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSkip(skip + limit)}
                      disabled={skip + limit >= total}
                      className="btn-secondary"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* User Details Modal */}
        <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">User Details</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-violet-500/20 flex items-center justify-center">
                    <span className="text-2xl font-bold text-violet-400">
                      {selectedUser.first_name?.charAt(0)}{selectedUser.last_name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-white">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </p>
                    <p className="text-slate-400">{selectedUser.email}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-800">
                  <div>
                    <p className="text-slate-500 text-sm">Account Type</p>
                    <p className="text-white capitalize">{selectedUser.account_type}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Subscription</p>
                    <Badge className={`capitalize ${getPlanBadge(selectedUser.subscription_plan)}`}>
                      {selectedUser.subscription_plan}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Assets</p>
                    <p className="text-white">{selectedUser.stats?.assets_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Active Leases</p>
                    <p className="text-white">{selectedUser.stats?.active_leases || 0}</p>
                  </div>
                </div>
                
                {selectedUser.service_requests?.length > 0 && (
                  <div className="pt-4 border-t border-slate-800">
                    <p className="text-slate-400 text-sm mb-2">Recent Service Requests</p>
                    <div className="space-y-2">
                      {selectedUser.service_requests.slice(0, 3).map((req) => (
                        <div key={req.request_id} className="p-3 rounded-lg bg-slate-800/50">
                          <div className="flex items-center justify-between">
                            <span className="text-white capitalize">{req.service_type.replace(/_/g, ' ')}</span>
                            <Badge className="capitalize">{req.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setSelectedUser(null)} className="btn-secondary">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Edit User</DialogTitle>
              <DialogDescription className="text-slate-400">
                {editUser?.first_name} {editUser?.last_name} ({editUser?.email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-slate-300 text-sm">Subscription Plan</label>
                <Select 
                  value={editForm.subscription_plan} 
                  onValueChange={(v) => setEditForm({ ...editForm, subscription_plan: v })}
                >
                  <SelectTrigger className="input-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditUser(null)} className="btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleSaveUser} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Confirmation */}
        <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
          <AlertDialogContent className="bg-slate-900 border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete User Account</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Are you sure you want to delete <strong>{deleteUser?.first_name} {deleteUser?.last_name}</strong> ({deleteUser?.email})?
                This will permanently remove the account and all associated data (assets, documents, leases). This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleting}
                data-testid="confirm-delete-user-btn"
              >
                {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : "Delete Account"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default AdminUsersPage;
